import type { DocumentReference } from '@medplum/fhirtypes';
import type { NotificationChannel, NotificationTypeDefinition } from '@polarissleep/shared';
import { ALL_NOTIFICATIONS, POLARIS_ENV_TAG_SYSTEM, withTenantScope } from '@polarissleep/shared';
import { TEMPLATE_TYPE_SYSTEM } from '../../../lib/template-renderers/constants';

/**
 * Template channel codes match the values that `apps/provider-app/scripts/seed-notification-templates.ts`
 * assigns to `DocumentReference.category[0].coding[0].code`. One registry NotificationChannel (EMAIL,
 * IN_APP, SMS) can map to more than one template channel — EMAIL fans out to subject + body.
 */
export const TEMPLATE_CHANNELS = ['email-subject', 'email-body', 'in-app-body', 'sms-body'] as const;
export type TemplateChannel = (typeof TEMPLATE_CHANNELS)[number];

/** Display label for each template channel, shown in the per-notification editor. */
export const TEMPLATE_CHANNEL_LABELS: Record<TemplateChannel, string> = {
  'email-subject': 'Email Subject',
  'email-body': 'Email Body',
  'in-app-body': 'In-App Body',
  'sms-body': 'SMS Body',
};

export interface ParsedTemplatePath {
  notificationKey: string;
  channel: TemplateChannel;
  filename: string;
}

/**
 * Parse a seeder-produced path like `emails/patient.system.new-provider-message/body.html.pug`
 * into its components. Returns null if the shape isn't `<top-dir>/<key>/<filename>` or the
 * derived channel isn't one of TEMPLATE_CHANNELS.
 */
export function parseTemplatePath(path: string): ParsedTemplatePath | null {
  const parts = path.split('/');
  if (parts.length !== 3) {
    return null;
  }
  const [topDir, notificationKey, filename] = parts;
  if (!topDir || !notificationKey || !filename) {
    return null;
  }
  const channel = deriveChannel(topDir, filename);
  if (!channel) {
    return null;
  }
  return { notificationKey, channel, filename };
}

function deriveChannel(topDir: string, filename: string): TemplateChannel | null {
  if (topDir === 'sms') {
    return 'sms-body';
  }
  if (topDir === 'emails') {
    if (filename.includes('subject')) {
      return 'email-subject';
    }
    return 'email-body';
  }
  if (topDir === 'in-app') {
    return 'in-app-body';
  }
  return null;
}

/**
 * Build the canonical template path for a notification key + template channel. Inverse of
 * parseTemplatePath. The filenames match what the seeder and static compiler expect.
 */
export function buildTemplatePath(notificationKey: string, channel: TemplateChannel): string {
  switch (channel) {
    case 'email-subject':
      return `emails/${notificationKey}/subject.txt.pug`;
    case 'email-body':
      return `emails/${notificationKey}/body.html.pug`;
    case 'in-app-body':
      return `in-app/${notificationKey}/body.html.pug`;
    case 'sms-body':
      return `sms/${notificationKey}/body.txt.pug`;
  }
}

/**
 * Expand a registry NotificationChannel to the template channel(s) it produces.
 * EMAIL → email-subject + email-body; IN_APP → in-app-body; SMS → sms-body.
 */
export function expandRegistryChannel(channel: NotificationChannel): TemplateChannel[] {
  switch (channel) {
    case 'EMAIL':
      return ['email-subject', 'email-body'];
    case 'IN_APP':
      return ['in-app-body'];
    case 'SMS':
      return ['sms-body'];
  }
}

/**
 * All template channels for a notification definition, returned in canonical order
 * (email-subject, email-body, in-app-body, sms-body). Only includes channels that the
 * registry says the notification supports.
 */
export function templateChannelsForNotification(def: NotificationTypeDefinition): TemplateChannel[] {
  const expanded = new Set(def.channels.flatMap(expandRegistryChannel));
  return TEMPLATE_CHANNELS.filter((c) => expanded.has(c));
}

/**
 * A path is "legacy" (i.e. not tied to a registry notification key) if it either fails to
 * parse into the seeder's three-segment shape or its middle segment isn't a known
 * notification key. Used to route legacy templates (new-message, task-assignment) into
 * the flat editor.
 */
export function isLegacyPath(path: string): boolean {
  const parsed = parseTemplatePath(path);
  if (!parsed) {
    return true;
  }
  return !(parsed.notificationKey in ALL_NOTIFICATIONS);
}

/** Convenience: pull the template path out of a DocumentReference's `type.text` field. */
export function getDocRefPath(docRef: DocumentReference): string | null {
  return docRef.type?.text ?? docRef.type?.coding?.[0]?.code ?? null;
}

export interface BuildDocumentReferenceInput {
  notificationKey: string;
  channel: TemplateChannel;
  content: string;
  env: string;
  envOrgId: string;
  /** Optional human-readable notification label/description; preferred when known. */
  notificationLabel?: string;
}

/**
 * Build a DocumentReference that matches the shape produced by the seeder at
 * `scripts/seed-notification-templates.ts`. This lets the admin UI create new templates
 * on-the-fly for channels that haven't been seeded yet, without drifting from the seeder's
 * canonical shape.
 */
export function buildDocumentReferenceForChannel(input: BuildDocumentReferenceInput): DocumentReference {
  const templateKey = buildTemplatePath(input.notificationKey, input.channel);
  const description = input.notificationLabel
    ? `Template for ${input.notificationKey} (${input.channel})`
    : `Template: ${templateKey}`;

  const docRef: DocumentReference = {
    resourceType: 'DocumentReference',
    status: 'current',
    type: {
      coding: [
        {
          system: TEMPLATE_TYPE_SYSTEM,
          code: templateKey,
          display: `Notification template: ${templateKey}`,
        },
      ],
      text: templateKey,
    },
    meta: {
      tag: [
        { system: TEMPLATE_TYPE_SYSTEM, code: 'notification-template' },
        { system: POLARIS_ENV_TAG_SYSTEM, code: input.env },
      ],
    },
    category: [
      {
        coding: [
          {
            system: TEMPLATE_TYPE_SYSTEM,
            code: input.channel,
            display: input.channel,
          },
        ],
      },
    ],
    description,
    content: [
      {
        attachment: {
          contentType: 'text/plain',
          data: btoa(input.content),
          title: templateKey,
        },
      },
    ],
  };

  return withTenantScope(docRef, input.envOrgId);
}
